// =============================================================================
// Project Management module — owner / EPC form (create/edit via the standard ModelFormPage)
//
// One component for both registries: they share the ProjectManagementParty shape, so the only
// difference is which model class it edits. This is the CANONICAL place to set a company's logo —
// the record is org-level, so the logo lands on every project referencing it at once.
// =============================================================================

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { Page } from '/imports/ui/Page';
import { Checkbox } from '/imports/ui/Checkbox';
import { ImageInput } from '/imports/ui/ImageInput';
import { ModelFormPage } from '/imports/ui/ModelFormPage';
import { NotPermittedPage } from '/imports/ui/NotPermittedPage';
import { useUser, useFindOne } from '/imports/hooks';
import { Accounts } from '/imports/api/accounts';
import { strings } from '/imports/strings';
import { ModelClass } from '/imports/types';
import {
  ProjectManagementParty, projectManagementEpcModelClass, projectManagementIsAdmin,
  projectManagementOwnerModelClass, projectManagementUtilityModelClass,
} from '/imports/api/projectManagement';

const PartyPageStyled = styled(Page)`
`;

/** Create/edit one party (name, logo, website, note). */
const PartyPage = ({ modelClass }: { modelClass: ModelClass<ProjectManagementParty, any> }) => {
  const { _id } = useParams<any>();
  const user = useUser();
  const account = useFindOne(() => Accounts.find({ orgId: user?.orgId, userId: user?._id }), [user?._id]);
  const defaultUpdate: Partial<ProjectManagementParty> = { _id, active: true };
  const [update, setUpdate] = useState<Partial<ProjectManagementParty>>(defaultUpdate);
  // Org-wide reference data, like portfolios — admin-only.
  if (!projectManagementIsAdmin(user, account)) return <NotPermittedPage />;
  return <ModelFormPage
    modelClass={modelClass}
    styled={PartyPageStyled}
    defaultUpdate={defaultUpdate}
    update={update}
    setUpdate={setUpdate}
    formSpec={[
      [{ field: 'active', input: { type: Checkbox } }],
      [{ field: 'name', label: strings.Name(), asterisk: true }],
      // Saved here rather than from a project: the record is shared, so one upload covers every
      // project referencing this company. Left blank, the server looks one up on create/rename.
      [{ field: 'logo', label: strings.Logo(), input: { type: ImageInput }, optional: true }],
      [{ field: 'website', label: strings.Website(), optional: true }],
      [{ field: 'note', label: strings.Note(), optional: true }],
    ]}
  />;
};

export const ProjectManagementOwnerPage = () => <PartyPage modelClass={projectManagementOwnerModelClass} />;
export const ProjectManagementEpcPage = () => <PartyPage modelClass={projectManagementEpcModelClass} />;
export const ProjectManagementUtilityPage = () => <PartyPage modelClass={projectManagementUtilityModelClass} />;
